import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider, Spin } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import antdTheme from './theme/antdTheme';
import AppLayout from './components/AppLayout';
import TeacherLayout from './components/TeacherLayout';
import Login from './pages/Login';
import { FEATURE_FLAGS } from './featureFlags';

// 页面按路由懒加载，拆分成独立 chunk，首屏只下载登录页
const Dashboard = lazy(() => import('./pages/Dashboard'));
// 家长端六宫格首页。跟 Dashboard 二选一，由 HomeIndex 按开关+角色分发。
const ParentDashboard = lazy(() => import('./pages/ParentDashboard'));
const AssessmentCenter = lazy(() => import('./pages/AssessmentCenter'));
const GrowthProfile = lazy(() => import('./pages/GrowthProfile'));
const AdaptiveAssessment = lazy(() => import('./pages/AdaptiveAssessment'));
const TeacherDashboard = lazy(() => import('./pages/teacher/TeacherDashboard'));
const StudentList = lazy(() => import('./pages/teacher/StudentList'));
const StudentProfile = lazy(() => import('./pages/teacher/StudentProfile'));
const AddStudent = lazy(() => import('./pages/teacher/AddStudent'));
const RecordEvent = lazy(() => import('./pages/teacher/RecordEvent'));
const AssessmentTasks = lazy(() => import('./pages/teacher/AssessmentTasks'));
const EventDetail = lazy(() => import('./pages/teacher/EventDetail'));
const TeacherMe = lazy(() => import('./pages/teacher/TeacherMe'));
const GrowthReport = lazy(() => import('./pages/GrowthReport'));
const ClassAlbum = lazy(() => import('./pages/ClassAlbum'));
const Mailbox = lazy(() => import('./pages/Mailbox'));
const Leave = lazy(() => import('./pages/Leave'));
const TeacherAttendance = lazy(() => import('./pages/teacher/TeacherAttendance'));
const Chain = lazy(() => import('./pages/Chain'));
const TeacherChain = lazy(() => import('./pages/teacher/TeacherChain'));
const Courses = lazy(() => import('./pages/Courses'));
const TeacherCourses = lazy(() => import('./pages/teacher/TeacherCourses'));
const FamilyServices = lazy(() => import('./pages/FamilyServices'));
const ChangePassword = lazy(() => import('./pages/ChangePassword'));
const ResetParentPassword = lazy(() => import('./pages/teacher/ResetParentPassword'));
// AI 成长大脑 + GE3000 家庭成长画像
const GrowthBrain = lazy(() => import('./pages/GrowthBrain'));
const FamilyEntry = lazy(() => import('./pages/family/FamilyEntry'));
const FamilyPrivacy = lazy(() => import('./pages/family/FamilyPrivacy'));
const FamilyStep = lazy(() => import('./pages/family/FamilyStep'));
const FamilyConfirm = lazy(() => import('./pages/family/FamilyConfirm'));
const FamilyGenerating = lazy(() => import('./pages/family/FamilyGenerating'));
const FamilyReport = lazy(() => import('./pages/family/FamilyReport'));
const TeacherFamily = lazy(() => import('./pages/teacher/TeacherFamily'));
const TeacherFamilyDetail = lazy(() => import('./pages/teacher/TeacherFamilyDetail'));
const TeacherGuide = lazy(() => import('./pages/teacher/TeacherGuide'));
const ResourceMap = lazy(() => import('./pages/teacher/ResourceMap'));
// 学年管理 / 班级管理 / 升年级中心（管理员，入口在「我的」页）
const AcademicYears = lazy(() => import('./pages/teacher/AcademicYears'));
const ClassManage = lazy(() => import('./pages/teacher/ClassManage'));
const PromotionCenter = lazy(() => import('./pages/teacher/PromotionCenter'));
const PromotionHistory = lazy(() => import('./pages/teacher/PromotionHistory'));
// 课表：一个查看页自适应三种角色，导入页只给 admin
const Schedule = lazy(() => import('./pages/Schedule'));
const ScheduleImport = lazy(() => import('./pages/teacher/ScheduleImport'));
// 社团 + 社团/晚自习点名。⚠️跟 TeacherAttendance（课堂考勤请假审核）是两套
// 独立功能，入口也分开放 —— 混在一个页面里会让老师分不清在给哪个场合点名
const ClubManage = lazy(() => import('./pages/teacher/ClubManage'));
const ClubDetail = lazy(() => import('./pages/teacher/ClubDetail'));
const TakeAttendance = lazy(() => import('./pages/teacher/TakeAttendance'));
const TeacherAdmin = lazy(() => import('./pages/teacher/TeacherAdmin'));
const TeacherScores = lazy(() => import('./pages/teacher/TeacherScores'));

const PageLoading = () => <Spin size="large" style={{ display: 'block', margin: '120px auto' }} />;

function PrivateRoute({ children, roles }: { children: React.ReactElement; roles?: string[] }) {
  const token = localStorage.getItem('token');
  if (!token) return <Navigate to="/login" />;
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  if (roles) {
    if (!roles.includes(user.role)) {
      // 角色不匹配：按角色送回各自的首页
      const home = user.role === 'teacher' || user.role === 'admin' ? '/teacher' : '/home';
      return <Navigate to={home} replace />;
    }
  }
  return children;
}

// 改密页专用守卫：只要登录了就能进（不能套 PrivateRoute，否则强制改密的人
// 会被守卫无限重定向到这一页自己）。
function AuthedRoute({ children }: { children: React.ReactElement }) {
  const token = localStorage.getItem('token');
  if (!token) return <Navigate to="/login" />;
  return children;
}

function RoleRedirect() {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  if (user.role === 'teacher' || user.role === 'admin') {
    return <Navigate to="/teacher" replace />;
  }
  return <Navigate to="/home" replace />;
}

/**
 * /home 首页发什么，由「开关 + 角色」两件事决定。
 *
 * ENABLE_GROWTH_PROFILE=false（当前）→ 家长看六宫格服务入口。
 *   原因不是嫌老首页不好看，是它对 60/62 个家庭全是空盒子
 *   （成长指数/雷达/优势/待提升都只认测评基线，而测评已收口）。
 *   实测数字见 featureFlags.ts。
 * 打开开关 → 家长回到 Dashboard 那套成长内容。
 *
 * ⚠️学生走哪条：学生**永远**是 Dashboard，不进六宫格。
 *   六宫格文案是家长视角（"家校同行"/"看看孩子的校园时光"），给学生看不对。
 *   线上 62 个 student 行的 username 都是 `_stu_` 前缀且不对外发
 *   （studentAdmin.js:239），所以这条分支实际走不到 —— 但写死它，
 *   将来真发学生账号时不会突然看到写给家长的话。
 * ⚠️别改成在 Dashboard.tsx 内部判断后 return 另一个组件：
 *   那样 Dashboard 的四个 useEffect 请求仍会发出去，家长首页白打四个接口，
 *   其中 /dashboard/student/:id 对没绑 parentOf 的账号还会 403 弹错。
 */
function HomeIndex() {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  if (user.role === 'parent' && !FEATURE_FLAGS.ENABLE_GROWTH_PROFILE) {
    return <ParentDashboard />;
  }
  return <Dashboard />;
}

function App() {
  return (
    <ConfigProvider locale={zhCN} theme={antdTheme}>
      <BrowserRouter>
        <Suspense fallback={<PageLoading />}>
        <Routes>
          <Route path="/login" element={<Login />} />

          {/* 改密页走 AuthedRoute：强制改密的人必须能进得来 */}
          <Route path="/change-password" element={<AuthedRoute><ChangePassword /></AuthedRoute>} />

          <Route path="/" element={<PrivateRoute><RoleRedirect /></PrivateRoute>} />

          {/* Student/Parent routes */}
          <Route path="/home" element={<PrivateRoute roles={['student', 'parent']}><AppLayout /></PrivateRoute>}>
            {/* 首页按开关+角色分发，见 HomeIndex 的注释 */}
            <Route index element={<HomeIndex />} />
            <Route path="services" element={<FamilyServices />} />
            {/* 测评整条线受 ENABLE_ASSESSMENT 控制。关闭时不注册路由，
                直接敲 /home/assessment 会落到本组下面的 path="*" 兜底回首页，
                而不是进到一个底栏里已经看不见的页面。
                ⚠️ 兜底是必须的：react-router v7 匹配不到子路由时 Outlet 渲染空，
                页面只剩底栏，看着像白屏 bug */}
            {FEATURE_FLAGS.ENABLE_ASSESSMENT && (
              <>
                <Route path="assessment" element={<AssessmentCenter />} />
                <Route path="assessment/start" element={<AdaptiveAssessment />} />
              </>
            )}
            {/* 成长画像受 ENABLE_GROWTH_PROFILE 控制，关闭时不注册路由 ——
                跟测评同一个处理方式，敲 URL 会被下面 path="*" 兜底回首页。
                ⚠️ 这一页整体走 /adaptive/latest，线上 61/62 个家庭返回 404 →
                   渲染 Empty。不是"还没测"，测评已收口所以不会再有记录。
                ⚠️ 这里关的只是**家长/学生端**这一页。教师端看学生能力雷达走的是
                   /teacher/student/:id（StudentProfile），不受影响，别一起关。 */}
            {FEATURE_FLAGS.ENABLE_GROWTH_PROFILE && (
              <Route path="profile" element={<GrowthProfile />} />
            )}
            <Route path="album" element={<ClassAlbum />} />
            <Route path="mailbox" element={<Mailbox />} />
            <Route path="leave" element={<Leave />} />
            <Route path="chain" element={<Chain />} />
            <Route path="courses" element={<Courses />} />
            {/* 课表。跟 /teacher/schedule 是同一个组件，内部按角色自适应：
                家长/学生只看自己孩子那个班，没有班级切换器（后端对别的班 403） */}
            <Route path="schedule" element={<Schedule />} />
            {/* AI 成长大脑。关闭时不注册 —— 四个卡片里两个指向 /home/profile
                （底栏「成长画像」tab）、两个指向 /home/family（家校服务页已有入口），
                纯粹多一层跳转，且「AI 成长建议」名不副实（ai_profiles 表 0 行）*/}
            {FEATURE_FLAGS.ENABLE_GROWTH_BRAIN && (
              <Route path="brain" element={<GrowthBrain />} />
            )}
            {/* GE3000 家庭成长画像。⚠️刻意不受 ENABLE_GROWTH_BRAIN 控制：
                这是家长填问卷的入口，也是成长大脑唯一的数据来源。
                把它一起关了就永远等不到数据，成长大脑也永远打不开 */}
            <Route path="family" element={<FamilyEntry />} />
            <Route path="family/privacy" element={<FamilyPrivacy />} />
            <Route path="family/step/:step" element={<FamilyStep />} />
            <Route path="family/confirm" element={<FamilyConfirm />} />
            <Route path="family/generating" element={<FamilyGenerating />} />
            <Route path="family/report" element={<FamilyReport />} />
            {/* 兜底：未注册的子路径（如测评关闭时的 assessment）回首页，不留白屏 */}
            <Route path="*" element={<Navigate to="/home" replace />} />
          </Route>

          {/* Teacher routes - mobile first */}
          <Route path="/teacher" element={<PrivateRoute roles={['teacher', 'admin']}><TeacherLayout /></PrivateRoute>}>
            <Route index element={<TeacherDashboard />} />
            <Route path="students" element={<StudentList />} />
            <Route path="students/add" element={<AddStudent />} />
            <Route path="student/:studentId" element={<StudentProfile />} />
            <Route path="student/:studentId/report" element={<GrowthReport />} />
            <Route path="record" element={<RecordEvent />} />
            <Route path="event/:eventId" element={<EventDetail />} />
            {/* 同上，关闭时不注册 */}
            {FEATURE_FLAGS.ENABLE_ASSESSMENT && (
              <Route path="assessments" element={<AssessmentTasks />} />
            )}
            <Route path="album" element={<ClassAlbum />} />
            <Route path="mailbox" element={<Mailbox />} />
            <Route path="attendance" element={<TeacherAttendance />} />
            <Route path="chain" element={<TeacherChain />} />
            <Route path="courses" element={<TeacherCourses />} />
            <Route path="reset-password" element={<ResetParentPassword />} />
            {/* AI 成长大脑 + 家庭画像（教师端）。整组受 ENABLE_GROWTH_BRAIN 控制。
                ⚠️ 这四条必须一起关：下面三个页面的**唯一入口**就是 brain 首页，
                只关 brain 会让它们变成没有入口的死功能（能跑但没人点得到）。
                全部依赖 family_assessments，那张表 0 行 —— 家长一份问卷都没填，
                现在放出去老师看到的是三个空页面 */}
            {FEATURE_FLAGS.ENABLE_GROWTH_BRAIN && (
              <>
                <Route path="brain" element={<GrowthBrain />} />
                <Route path="family" element={<TeacherFamily />} />
                <Route path="family/:studentId" element={<TeacherFamilyDetail />} />
                <Route path="guide" element={<TeacherGuide />} />
                <Route path="resource-map" element={<ResourceMap />} />
              </>
            )}
            <Route path="me" element={<TeacherMe />} />
            {/* 学年制管理。⚠️这四个页面只对 admin 有意义，但路由仍挂在
                teacher|admin 的 TeacherLayout 下 —— 页面内部的接口全是
                requireRole('admin')，老师误敲 URL 进来只会看到空列表和 403 提示。
                单独加一层角色守卫会让「班主任兼管理员」这种账号进不来 */}
            <Route path="academic-years" element={<AcademicYears />} />
            <Route path="classes" element={<ClassManage />} />
            <Route path="promotion" element={<PromotionCenter />} />
            <Route path="promotion-history" element={<PromotionHistory />} />
            {/* 课表。导入只对 admin 有意义（接口是 requireRole('admin')），
                查看所有角色都能进 —— 家长/学生走 /home/schedule 那条 */}
            <Route path="schedule" element={<Schedule />} />
            <Route path="schedule-import" element={<ScheduleImport />} />
            {/* 教师账号管理。后端 /api/teacher-admin 全部接口都是 requireRole('admin')，
                这里不额外做前端拦截 —— 入口只对 admin 显示，直接敲 URL 进来也只会
                看到空列表 + 401，不会泄露数据 */}
            <Route path="admin/teachers" element={<TeacherAdmin />} />
            {/* 社团 + 点名。⚠️这三页对**普通教师**开放，不是 admin 专属：
                已定案「教师自己就能建社团」，而点名权按社团 advisorIds 判
                （社团跳班，班级口径卡不住）。后端 routes/club.js 和
                attendanceSession.js 是 requireRole('teacher','admin') + 逐条
                查指导老师身份，前端不重复拦 */}
            <Route path="clubs" element={<ClubManage />} />
            <Route path="club/:id" element={<ClubDetail />} />
            <Route path="take-attendance" element={<TakeAttendance />} />
            <Route path="scores" element={<TeacherScores />} />
            {/* 兜底：未注册的子路径（如测评关闭时的 assessments）回教师首页 */}
            <Route path="*" element={<Navigate to="/teacher" replace />} />
          </Route>

          {/* Shared report page (parent/student view) */}
          <Route path="/report/:studentId" element={<PrivateRoute><GrowthReport /></PrivateRoute>} />
        </Routes>
        </Suspense>
      </BrowserRouter>
    </ConfigProvider>
  );
}

export default App;
